<template>
  <v-container class="invoices">
    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="12"
        lg="8"
        xl="6"
        offset-lg="2"
        offset-xl="3"
        pt="0"
      >
        <h1>
          {{ $t('invoices.txt.invoices') }}
        </h1>
        <div
          v-if="loading"
          class="loader"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>
        <div
          v-if="emptyInvoices"
          class="no-results"
        >
          <v-icon>mdi-file-document</v-icon>
          <br>{{ $t('invoices.txt.empty_invoices') }}
        </div>
      </v-col>
    </v-row>
    <v-row class="list">
      <v-col
        v-for="invoice in visibleItemsPerPages"
        :key="invoice.invoiceId"
        cols="12"
        sm="12"
        md="12"
        lg="8"
        xl="6"
        offset-lg="2"
        offset-xl="3"
        class="py-sm-1"
      >
        <v-card class="item">
          <v-container
            fluid
            class="pa-0"
          >
            <v-row>
              <v-col
                cols="12"
                sm="3"
                class="align-center text-center text-sm-left"
              >
                {{ invoice.invoiceDate | formatDate }}
              </v-col>
              <v-col
                cols="12"
                sm="3"
                class="align-center text-center text-sm-right"
              >
                <b>{{ invoice.amount | formatPrice(currency.symbol) }}</b>
              </v-col>
              <v-col
                v-if="invoice.status==='Posted'"
                cols="12"
                sm="3"
                class="align-center text-center paid"
              >
                <v-icon color="peacock">
                  mdi-check-bold
                </v-icon>
                <span> {{ $t('invoices.txt.posted') }} </span>
              </v-col>
              <v-col
                v-else
                cols="12"
                sm="3"
                class="align-center text-center"
              >
                <div
                  v-if="invoice.status === 'Paid'"
                  class="paid"
                >
                  <v-icon
                    class="mr-1"
                    color="peacock"
                  >
                    mdi-check-bold
                  </v-icon>
                  {{ $t('invoices.txt.paid') }}
                </div>
                <div
                  v-else
                  class="open"
                >
                  <v-icon
                    class="mr-1"
                    color="reddish"
                  >
                    mdi-close
                  </v-icon>
                  {{ $t('invoices.txt.open') }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="3"
                md="3"
                class="align-center text-center text-sm-right"
              >
                <v-btn
                  class="button elevation-0 float-sm-right"
                  color="ice"
                  @click="downloadInvoice(invoice)"
                >
                  {{ $t('invoices.txt.download') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
      <v-col
        v-if="filteredList.length > 0"
        cols="12"
        class="pagination mt-4"
      >
        <v-pagination
          v-model="page"
          color="pale"
          prev-icon="mdi-arrow-left"
          next-icon="mdi-arrow-right"
          :length="Math.ceil(filteredList.length/itemsPerPage)"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import FileSaver from 'file-saver'
import { mapState } from 'vuex'

export default {
  name: 'Invoices',
  data() {
    return {
      page: 1,
      pageCount: 0,
      itemsPerPage: 7,
      invoices: [],
      loading: false
    }
  },
  computed: {
    ...mapState('account', ['currency']),
    filteredList() {
      const filteredInvoices = this.invoices.filter(invoice => invoice.amount !== 0)
      filteredInvoices.sort((x, y) => {
        const dateX = x.invoiceDate.split('-').join('')
        const dateY = y.invoiceDate.split('-').join('')

        if (dateX > dateY) {
          return -1
        } else if (dateX < dateY) {
          return 1
        }
        return 0
      })

      return filteredInvoices
    },
    visibleItemsPerPages() {
      const visibleVehiclesPerPage = this.filteredList
      return visibleVehiclesPerPage.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      )
    },
    emptyInvoices() {
      return this.invoices && this.invoices.length == 0 && !this.loading
    }
  },
  async created() {
    await this.getInvoices()
  },
  methods: {
    getInvoices() {
      this.loading = true
      return apiCalls
        .get(api.InvoiceGetAll, '', '')
        .then(res => (this.invoices = res.data))
        .finally(() => (this.loading = false))
    },
    downloadInvoice(invoice) {
      return apiCalls
        .downloadFile(api.InvoiceDownload, invoice.invoiceId, '')
        .then(res => {
          if (res.status == 200) {
            FileSaver.saveAs(
              new Blob([res.data]),
              invoice.invoiceNumber + '_' + invoice.invoiceDate + '.pdf'
            )
          }
        })
    }
  }
}
</script>

<style lang="scss">
.invoices {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .list {
    .item {
      border-radius: 6px;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.18);
      color: #4a5568;
      padding: 12px 24px;

      .paid {
        color: var(--v-peacock-base);
      }

      .open {
        color: var(--v-reddish-base);
      }

      .button {
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        color: var(--v-mid-base) !important;
        height: 32px;
        border-radius: 3px;
      }
    }
  }
  .pagination {
    margin: 0 auto;
    .v-pagination {
      &__item {
        font-weight: bold;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
        margin: 0 8px;
        &:focus {
          outline: none !important;
        }
        &--active {
          background-color: var(--v-secondary-base) !important;
          color: var(--v-darkgrey-blue-base) !important;
          box-shadow: none;
        }
      }
      &__navigation {
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
        margin: 0 40px;
      }
      .v-icon {
        font-size: 1.25rem;
        color: var(--v-slate-base);
      }
    }
  }
}
</style>
